import { type PlanType } from '@/types';

import { PLAN_FEATURES, type PlanFeatureType } from './consts';

type Region = 'KR' | 'GLOBAL';

/**
 * 사용자 플랜에 따른 기능 접근 권한을 관리하는 클래스
 * 사용자의 국가 코드에 따라 지역별(KR/GLOBAL) 기능 정책을 적용합니다.
 */
export class UserPlanManager {
  private region: Region;
  private userPlanType: PlanType;

  /**
   * UserPlanManager 인스턴스를 생성합니다.
   * @param userCountryCode - 사용자의 국가 코드 (KR이면 한국 정책, 그 외는 글로벌 정책 적용)
   * @param userPlanType - 사용자의 플랜 타입
   */
  constructor(userCountryCode: string, userPlanType: PlanType) {
    this.region = userCountryCode === 'KR' ? 'KR' : 'GLOBAL';
    this.userPlanType = userPlanType;
  }

  /**
   * 특정 기능을 사용하기 위해 필요한 최소 플랜을 반환합니다.
   * @param feature - 확인할 기능
   * @returns 해당 기능을 사용할 수 있는 최소 플랜 ('BASIC' | 'PRO')
   * @example
   * ```typescript
   * const manager = new UserPlanManager('KR', 'FREE');
   * const minPlan = manager.getFeaturePlanType('PRIVATE_QUIZ');
   * // 결과: 'BASIC' (BASIC 플랜부터 사용 가능)
   * ```
   */
  getFeaturePlanType(feature: PlanFeatureType): Exclude<PlanType, 'FREE'> {
    if (PLAN_FEATURES[this.region]['BASIC'].has(feature)) return 'BASIC';
    return 'PRO';
  }

  /**
   * 사용자가 특정 기능에 대한 접근 권한이 있는지 확인합니다.
   * @param feature - 확인할 기능
   * @returns 해당 기능에 접근 가능하면 true, 그렇지 않으면 false
   * @example
   * ```typescript
   * const manager = new userPlanManager('KR', 'PRO');
   * const canUse = manager.canAccessFeature('AI_TUTOR');
   * // 결과: true
   * ```
   */
  canAccessFeature(feature: PlanFeatureType): boolean {
    return PLAN_FEATURES[this.region][this.userPlanType].has(feature);
  }

  /**
   * 사용자의 현재 플랜이 특정 기능에 필요한 플랜 레벨을 충족하는지 확인합니다.
   *
   * @param comparePlanType - 필요한 최소 플랜 타입
   * @returns 사용자 플랜이 요구사항을 충족하면 true, 그렇지 않으면 false
   *
   * 플랜 계층 구조: FREE < BASIC < PRO
   * - FREE 기능: 모든 사용자 사용 가능
   * - BASIC 기능: BASIC 또는 PRO 플랜 사용자만 사용 가능
   * - PRO 기능: PRO 플랜 사용자만 사용 가능
   */
  hasEnoughPlanType(comparePlanType: PlanType): boolean {
    if (comparePlanType === 'FREE') return true;
    else if (comparePlanType === 'BASIC') return this.userPlanType !== 'FREE';
    else return this.userPlanType === 'PRO';
  }
}
